import axios from "axios";

const errorCodes = {
    badRequest: "badRequest",
    unauthorized: "unauthorized",
    forbidden: "forbidden",
    notFound: "notFound",
    serverError: "serverError",
    unexpected: "unexpected",
    invalidCredentials: "invalidCredentials",
};

class API {
    constructor(collection) {
        this.collectionUrl = `${process.env.REACT_APP_API_BASE_URL}/${collection}`;
    }

    _handleError(error) {
        // Función auxiliar para detectar si la respuesta es HTML
        const isHtmlResponse = (data) => {
            if (typeof data === "string") {
                return (
                    data.trim().startsWith("<!DOCTYPE") ||
                    data.trim().startsWith("<html")
                );
            }
            return false;
        };

        // Función auxiliar para extraer mensaje de error o usar mensaje por defecto
        const getErrorMessage = (data, defaultMessage) => {
            // Si es HTML, no mostrar el HTML, usar mensaje por defecto
            if (isHtmlResponse(data)) {
                return defaultMessage;
            }
            // Si es un objeto con estructura de error de DRF
            if (typeof data === "object" && data !== null) {
                return data;
            }
            // Si es un string que no es HTML
            if (typeof data === "string" && !isHtmlResponse(data)) {
                return data;
            }
            return defaultMessage;
        };

        if (error.response?.status === 400)
            return Promise.reject({
                code: errorCodes.badRequest,
                message: getErrorMessage(
                    error.response.data,
                    "Error de validación"
                ),
            });
        if (error.response?.status === 401)
            return Promise.reject({
                code: errorCodes.unauthorized,
                message: getErrorMessage(error.response?.data, "No autorizado"),
            });
        if (error.response?.status === 403)
            return Promise.reject({
                code: errorCodes.forbidden,
                message: getErrorMessage(
                    error.response?.data,
                    "Acceso prohibido"
                ),
            });
        if (error.response?.status === 404)
            return Promise.reject({
                code: errorCodes.notFound,
                message: getErrorMessage(error.response?.data, "No encontrado"),
            });
        if (error.response?.status >= 500 && error.response?.status <= 599)
            return Promise.reject({
                code: errorCodes.serverError,
                message: getErrorMessage(
                    error.response?.data,
                    "Error del servidor. Por favor, intente nuevamente."
                ),
            });
        return Promise.reject({
            code: errorCodes.unexpected,
            message: getErrorMessage(
                error.response?.data,
                error.message || "Error inesperado"
            ),
        });
    }

    async getMulti(
        ordering = null,
        search = null,
        limit = null,
        offset = null,
        extra = {}
    ) {
        const params = { limit, offset, ...extra };
        if (ordering) params.order_by = ordering;
        if (search && search.length > 0) params.search = search;
        try {
            const response = await axios.get(`${this.collectionUrl}/`, {
                params,
            });
            return Promise.resolve(response.data);
        } catch (error) {
            return this._handleError(error);
        }
    }

    async getOne(id) {
        try {
            const response = await axios.get(`${this.collectionUrl}/${id}/`);
            return Promise.resolve(response.data);
        } catch (e) {
            return this._handleError(e);
        }
    }

    async create(data) {
        try {
            const response = await axios.post(`${this.collectionUrl}/`, data);
            return Promise.resolve(response.data);
        } catch (e) {
            return this._handleError(e);
        }
    }

    async update(id, data, partial = false) {
        try {
            let response = null;
            if (partial)
                response = await axios.patch(
                    `${this.collectionUrl}/${id}/`,
                    data
                );
            else
                response = await axios.put(
                    `${this.collectionUrl}/${id}/`,
                    data
                );
            return Promise.resolve(response.data);
        } catch (e) {
            return this._handleError(e);
        }
    }

    async delete(id) {
        try {
            const response = await axios.delete(`${this.collectionUrl}/${id}/`);
            return response;
        } catch (e) {
            return this._handleError(e);
        }
    }
}

class UserAPI extends API {
    async getToken(email, password) {
        try {
            const response = await axios.post(`${this.collectionUrl}/token/`, {
                email,
                password,
            });
            return Promise.resolve(response.data);
        } catch (e) {
            return Promise.reject(errorCodes.invalidCredentials);
        }
    }

    async changePassword(newPass) {
        try {
            const response = await axios.post(
                `${this.collectionUrl}/me/change-password/`,
                { new: newPass }
            );
            return Promise.resolve(response.data);
        } catch (e) {
            return this._handleError(e);
        }
    }

    async getMe() {
        try {
            const response = await axios.get(`${this.collectionUrl}/me/`);
            return Promise.resolve(response.data);
        } catch (e) {
            return this._handleError(e);
        }
    }

    async getDoctors() {
        try {
            const response = await axios.get(`${this.collectionUrl}/doctors/`);
            return Promise.resolve(response.data);
        } catch (error) {
            return this._handleError(error);
        }
    }

    async getPublicDoctors(ordering = null, search = null) {
        const params = {};
        if (ordering) params.order_by = ordering;
        if (search && search.length > 0) params.search = search;
        try {
            const response = await axios.get(
                `${this.collectionUrl}/public-doctors/`,
                { params }
            );
            return Promise.resolve(response.data);
        } catch (error) {
            return this._handleError(error);
        }
    }
}

class PasswordAPI extends API {
    async sendEmail(email) {
        try {
            const response = await axios.post(`${this.collectionUrl}/`, {
                email: email,
            });
            return Promise.resolve(response.data);
        } catch (e) {
            return this._handleError(e);
        }
    }

    async changePassword(password, uuid, token) {
        try {
            // eslint-disable-next-line no-unused-vars
            const response = await axios.post(
                `${this.collectionUrl}/${uuid}/${token}/`,
                { password: password }
            );
        } catch (e) {
            return this._handleError(e);
        }
    }
}

class DownloadAPI extends API {
    async get(consultation) {
        try {
            const response = await axios.post(
                `${this.collectionUrl}/export/`,
                consultation,
                {
                    responseType: "blob",
                }
            );
            return Promise.resolve(response.data);
        } catch (e) {
            return Promise.reject(e);
        }
    }
}

class ReportsExportAPI extends API {
    async export(reportId, printWithInsurance = true) {
        try {
            const response = await axios.post(
                `${this.collectionUrl}/`,
                { report: reportId, printWithInsurance: printWithInsurance },
                {
                    responseType: "blob",
                }
            );
            return Promise.resolve(response.data);
        } catch (e) {
            return Promise.reject(e);
        }
    }
}

class PatientsAPI extends API {
    async getByGovId(govId) {
        try {
            const response = await axios.get(`${this.collectionUrl}/by-gov-id/`, {
                params: { gov_id: govId },
            });
            return Promise.resolve(response.data);
        } catch (e) {
            return this._handleError(e);
        }
    }

    async getHistory(id, order) {
        try {
            const response = await axios.get(
                `${this.collectionUrl}/${id}/history/`,
                { params: { order_by: order } }
            );
            return Promise.resolve(response.data);
        } catch (e) {
            return this._handleError(e);
        }
    }

    async exportClinicalHistory(id) {
        try {
            const response = await axios.get(
                `${this.collectionUrl}/${id}/clinical-history/export/`,
                { responseType: "blob" }
            );
            return Promise.resolve(response.data);
        } catch (e) {
            return this._handleError(e);
        }
    }
}

class TurnAPI extends API {
    async check(data) {
        try {
            const response = await axios.post(`${this.collectionUrl}/`, data);
            return Promise.resolve(response.data);
        } catch (e) {
            return Promise.reject(e);
        }
    }
}

class ReportsAPI extends API {
    async getReports(params) {
        try {
            var request = {
                params: params,
            };
            const response = await axios.get(`${this.collectionUrl}/`, request);
            return Promise.resolve(response.data);
        } catch (error) {
            return this._handleError(error);
        }
    }
}

class AvailabilityAPI extends API {
    async getAvailableDates(doctorId, month, year, unscheduled = false) {
        try {
            const response = await axios.post(`${this.collectionUrl}/`, {
                doctor_id: doctorId,
                month,
                year,
                unscheduled,
            });
            return Promise.resolve(response.data);
        } catch (error) {
            return this._handleError(error);
        }
    }

    async getAvailableTimes(doctorId, date, unscheduled = false) {
        try {
            const response = await axios.post(`${this.collectionUrl}/`, {
                doctor_id: doctorId,
                date,
                unscheduled,
            });
            return Promise.resolve(response.data);
        } catch (error) {
            return this._handleError(error);
        }
    }
}

class ConfigsAPI extends API {
    async getSystemConfig() {
        try {
            const response = await axios.get(`${this.collectionUrl}/system/`);
            return Promise.resolve(response.data);
        } catch (error) {
            return this._handleError(error);
        }
    }

    async getSystemStatus() {
        try {
            const response = await axios.get(`${this.collectionUrl}/system/status/`);
            return Promise.resolve(response.data);
        } catch (error) {
            return this._handleError(error);
        }
    }

    async updateSystemConfig(id, data) {
        try {
            const formData = new FormData();
            if (data.logo) {
                formData.append("logo", data.logo);
            }
            const response = await axios.patch(
                `${this.collectionUrl}/system/${id}/`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );
            return Promise.resolve(response.data);
        } catch (error) {
            return this._handleError(error);
        }
    }
}

const api = {
    districts: new API("districts"),
    consultations: new API("consultations"),
    myConsultations: new API("consultations/mine"),
    establishments: new API("establishments"),
    patients: new PatientsAPI("patients"),
    documents: new API("patient-documents"),
    medicines: new API("medicines"),
    surgeries: new API("surgeries"),
    cities: new API("cities"),
    insurances: new API("insurances"),
    specialties: new API("specialties"),
    diagnostics: new API("diagnostics"),
    refraction: new DownloadAPI("consultations/refraction"),
    medication: new DownloadAPI("consultations/medication"),
    ophthalmologicalStudy: new DownloadAPI(
        "consultations/ophthalmological-study"
    ),
    clinicalStudy: new DownloadAPI("consultations/clinical-study"),
    surgeryOrder: new DownloadAPI("consultations/surgery-order"),
    surgicalEvaluation: new DownloadAPI("consultations/surgical-evaluation"),
    doctorsNote: new DownloadAPI("consultations/doctors-note"),
    consultationAttendance: new DownloadAPI(
        "consultations/consultation-attendance"
    ),
    visionEvolution: new API("vision-evolution"),
    tensionEvolution: new API("tension-evolution"),
    checkTurn: new TurnAPI("turns/check-turn"),
    generalData: new ReportsAPI("general-data-report"),
    medicationReport: new ReportsAPI("medication-report"),
    studyReport: new ReportsAPI("study-report"),
    surgeryReport: new ReportsAPI("surgery-report"),
    locations: new API("locations"),
    users: new UserAPI("users"),
    reset_password: new PasswordAPI("reset-password"),
    medicalOffices: new API("medical-offices"),
    turns: new API("turns"),
    notices: new API("notices"),
    images: new API("images"),
    doctors: new API("users/doctors"),
    appointments: new API("appointments"),
    publicAppointments: new API("appointments/public"),
    schedules: new API("schedules"),
    availability: new AvailabilityAPI("schedules/availability"),
    schedulesBlock: new API("schedule-blocks"),
    reports: new API("reports"),
    reportsExport: new ReportsExportAPI("reports/report/export"),
    configs: new ConfigsAPI("configs"),
};

export default api;
export { errorCodes, API, UserAPI };
